/**
 * The felt: one turn, drawn.
 *
 * Solo and online both render this exact component from a `View`, which is why
 * the two modes cannot drift apart visually or behaviourally. The only thing
 * that differs between them is who produces the next `View` — a reducer in this
 * tab, or the droplet.
 *
 * It is deliberately a dumb component. It decides nothing about the rules; it
 * asks the engine's own predicates (`canBank`, `heldScore`) so that the button
 * you can press is exactly the move the referee would accept.
 */

import { useState } from 'react';
import {
  canBank as engineCanBank,
  canPiggyback as engineCanPiggyback,
  heldScore as engineHeldScore,
  pendingTotal as enginePendingTotal,
  type View,
} from '@shared/engine';
import { allKeeps, RULE_CARD } from '@shared/scoring';
import { Die } from '../art/Die';
import { Eyebrow, cx, fmt } from '../ui/shell';

interface Props {
  view: View;
  /** The seat this browser is playing. Null while watching. */
  youId: string | null;
  onRoll: () => void;
  onToggle: (index: number) => void;
  onClear: () => void;
  onBank: () => void;
  /** Pick up the previous player's leftovers instead of throwing six. */
  onTakeCarry: () => void;
  /** Rendered in the top bar next to Scoring — "Leave", usually. */
  action?: { label: string; onClick: () => void };
  /** Shown above the turn card. The lobby code, a reconnect notice. */
  notice?: React.ReactNode;
}

/**
 * The engine predicates want a `GameState`, which is a `View` plus the PRNG seed
 * the client is never given. They read every field except that one, so a zero
 * stands in — and the alternative, duplicating the rules on the client, is how
 * the button you can press stops matching the move the server accepts.
 */
const asState = (view: View) => ({ ...view, rng: 0 });

export function Table({ view, youId, onRoll, onToggle, onClear, onBank, onTakeCarry, action, notice }: Props) {
  const [showRules, setShowRules] = useState(false);

  const player = view.players[view.turn]!;
  const yours = player.id === youId;
  const state = asState(view);
  const held = engineHeldScore(state);
  const pending = enginePendingTotal(state);
  const mayBank = yours && engineCanBank(state);
  // The offer is drawn whenever it exists, so a spectator can watch someone
  // agonise over it; only the player on turn gets a live button.
  const carry = view.carry;
  const mayTakeCarry = yours && engineCanPiggyback(state);

  const inPlay = view.dice.map((d, i) => ({ ...d, i })).filter((d) => d.state !== 'set');
  const setAside = view.dice.map((d, i) => ({ ...d, i })).filter((d) => d.state === 'set');
  const liveLeft = inPlay.filter((d) => d.state === 'live').length;
  const throwCount = liveLeft === 0 ? 6 : liveLeft;

  // Which dice appear in ANY legal keep. The rest are dead this throw, and
  // dimming them is the difference between reading the dice and counting them.
  const usable = new Set<number>();
  for (const keep of allKeeps(inPlay.map((d) => d.face))) {
    for (const idx of keep.indices) usable.add(inPlay[idx]!.i);
  }

  const needsOpener = !player.onBoard && pending < view.rules.threshold;
  const leadScore = Math.max(...view.players.map((p) => p.score));

  return (
    <>
      <div className="topbar">
        <div>
          <Eyebrow>Round {view.round}</Eyebrow>
          <div className="meta">to {fmt(view.rules.target)}</div>
        </div>
        <div className="btn-row">
          <button type="button" className="btn btn--small" onClick={() => setShowRules(!showRules)}>
            Scoring
          </button>
          {action && (
            <button type="button" className="btn btn--small" onClick={action.onClick}>
              {action.label}
            </button>
          )}
        </div>
      </div>

      {showRules && <RuleSheet piggyback={view.rules.piggyback} />}
      {notice}

      {view.closerId !== null && (
        <div className="banner">
          <Eyebrow tone="ivory">
            {view.players.find((p) => p.id === view.closerId)?.name} is out — last turns
          </Eyebrow>
        </div>
      )}

      {/* ── the turn ── */}
      <div className="card">
        {view.phase === 'farkle' && (
          <div className="stamp-wrap">
            <div className="stamp is-stamped">FARKLE</div>
          </div>
        )}

        <div className="turn-head">
          <div className="turn-name">{yours ? 'You' : player.name}</div>
          <div className="turn-total">
            <Eyebrow tone="ink">Total</Eyebrow>
            <div className="num">{fmt(player.score)}</div>
          </div>
        </div>

        <div className="rule" />

        <div className="turn-body">
          <Eyebrow tone="ink">This turn</Eyebrow>
          <div className="turn-points">{fmt(pending)}</div>
          <div className={cx('turn-note', needsOpener && pending > 0 && 'turn-note--warn')}>
            {view.phase === 'farkle'
              ? 'nothing scored — the turn is over'
              : needsOpener
                ? `needs ${fmt(view.rules.threshold)} to get on the board`
                : pending > 0
                  ? `banks to ${fmt(player.score + pending)}`
                  : yours
                    ? 'throw the dice'
                    : `${player.name} is up`}
          </div>
        </div>
      </div>

      {/* ── what the last player left, if anything ── */}
      {carry ? (
        <div className="tray carry rise">
          <div className="tray-head">
            {/* No `tone`: the default eyebrow is already brass, which is the
                colour this block wants. */}
            <Eyebrow>
              {carry.fromName} stopped on {carry.dice} {carry.dice === 1 ? 'die' : 'dice'}
            </Eyebrow>
            <span className="num carry-points">{fmt(carry.points)}</span>
          </div>
          <div className="dice-row">
            {carry.kept.map((face, i) => (
              <Die key={i} face={face} size="sm" set />
            ))}
          </div>
          <div className="carry-note">
            {mayTakeCarry
              ? `Take the ${carry.dice === 1 ? 'die' : 'dice'} and you start on ${fmt(carry.points)} — farkle and it all goes.`
              : yours
                ? `You have to be on the board before you can pick those up.`
                : `${player.name} can take them or throw six.`}
          </div>
        </div>
      ) : (
        /* ── set aside ── */
        <div className="tray">
          <div className="tray-head">
            <Eyebrow tone="dim">
              Set aside{view.hotStreak > 0 && ` — hot dice ×${view.hotStreak}`}
            </Eyebrow>
            {view.turnPoints > 0 && <span className="num">{fmt(view.turnPoints)}</span>}
          </div>
          <div className="dice-row">
            {setAside.length === 0 ? (
              <div className="tray-empty">nothing set aside yet</div>
            ) : (
              setAside.map((d) => <Die key={d.i} face={d.face} size="sm" set />)
            )}
          </div>
        </div>
      )}

      {/* ── the throw ── */}
      <div className="dice-row dice-row--main">
        {inPlay.map((d) => (
          // Keyed by throw so identical faces still tumble: three 4s twice in a
          // row is common, and dice that do not move read as a frozen app. The
          // animation lives on this wrapper rather than the die — see styles.css.
          <span
            key={`${view.throwId}-${d.i}`}
            className={cx('die-slot', view.phase !== 'idle' && 'tumble')}
          >
            <Die
              face={d.face}
              held={d.state === 'held'}
              dead={view.phase === 'pick' && !usable.has(d.i)}
              blank={view.phase === 'idle'}
              onClick={yours && view.phase === 'pick' ? () => onToggle(d.i) : undefined}
            />
          </span>
        ))}
      </div>

      <div className="hint">
        {view.phase === 'pick' && held === null
          ? 'that set has a die that scores nothing'
          : view.phase === 'pick' && held !== null && held > 0
            ? `selection +${fmt(held)}`
            : view.phase === 'pick' && yours
              ? 'tap the dice you want to keep'
              : ''}
      </div>

      {/* ── the decision ── */}
      {view.phase !== 'over' && (
        <div className="btn-row action-row">
          {view.phase === 'idle' ? (
            mayTakeCarry && carry ? (
              // Taking is the interesting move, so it gets the primary weight —
              // but throwing six stays a full-width button beside it, because a
              // rule that is awkward to decline is a rule that plays itself.
              <>
                <button type="button" className="btn btn--outline" onClick={onRoll}>
                  THROW SIX
                </button>
                <button type="button" className="btn btn--primary" onClick={onTakeCarry}>
                  TAKE {carry.dice} FOR {fmt(carry.points)}
                </button>
              </>
            ) : (
              <button type="button" className="btn btn--primary" onClick={onRoll} disabled={!yours}>
                {yours ? 'THROW SIX' : `${player.name.toUpperCase()} IS UP`}
              </button>
            )
          ) : view.phase === 'farkle' ? (
            <button type="button" className="btn btn--primary" disabled>
              PASSING…
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn--outline"
                onClick={onRoll}
                disabled={!yours || held === null || held === 0}
              >
                THROW {throwCount}
              </button>
              <button type="button" className="btn btn--primary" onClick={onBank} disabled={!mayBank}>
                BANK {fmt(pending)}
              </button>
            </>
          )}
        </div>
      )}

      {yours && view.phase === 'pick' && held !== 0 && (
        <button type="button" className="btn btn--ghost clear-btn" onClick={onClear}>
          Put them back
        </button>
      )}

      {/* ── standings ── */}
      <div className="spacer" />
      <Eyebrow className="field-label">Table</Eyebrow>
      <div className="standings">
        {view.players.map((p, i) => {
          const last = p.turns[p.turns.length - 1];
          return (
            <div
              key={p.id}
              className={cx(
                'row',
                i === view.turn && 'row--turn',
                i !== view.turn && p.score === leadScore && p.score > 0 && 'row--lead',
              )}
            >
              <span className="who">
                {p.name}
                {p.id === youId ? ' (you)' : ''}
              </span>
              {p.isBot && <span className="tag">cpu</span>}
              {!p.onBoard && p.score === 0 && <span className="tag">off board</span>}
              {last !== undefined && (
                <span className={cx('last', last === 0 && 'last--farkle')}>
                  {last === 0 ? 'farkle' : `+${fmt(last)}`}
                </span>
              )}
              <span className="score">{fmt(p.score)}</span>
            </div>
          );
        })}
      </div>

      <Log view={view} />
    </>
  );
}

function RuleSheet({ piggyback }: { piggyback: boolean }) {
  return (
    <div className="well--dark rise rule-sheet">
      <Eyebrow className="field-label">How dice score</Eyebrow>
      <div className="rules">
        {RULE_CARD.map(([what, worth]) => (
          <div key={what}>
            <span>{what}</span>
            <span>{worth}</span>
          </div>
        ))}
      </div>
      <div className="fineprint">
        Four of a kind and up are flat, and a hand is always read the way that pays most — four 1s is 1,100, not
        1,000. Clear all six dice and you throw all six again.
      </div>
      {piggyback && (
        <div className="fineprint">
          <strong>Piggyback:</strong> if the player before you banked with dice still in the cup, and you are on the
          board, you may pick those dice up instead of throwing six — and start on a copy of their score. They keep
          theirs. Farkle on that throw and you lose the lot.
        </div>
      )}
    </div>
  );
}

/** The last few things that happened. Enough to explain a score that moved. */
function Log({ view }: { view: View }) {
  const tail = view.log.slice(-4).reverse();
  if (tail.length === 0) return null;
  return (
    <div className="log">
      {tail.map((entry, i) => (
        <div key={`${view.throwId}-${i}`}>{entry.text}</div>
      ))}
    </div>
  );
}
